import { Card, CardHeader, CardBody } from '@heroui/react'
import type { AnalyticsSummary } from '../../types'

interface ConversionFunnelProps {
  data: AnalyticsSummary
}

export function ConversionFunnel({ data }: ConversionFunnelProps) {
  const { metrics } = data

  // Calculate stages
  const impressions = metrics.impressions
  const conversions = metrics.conversions
  const views = impressions > 0 ? Math.floor(impressions * 0.7) : 0 // Estimated views (70% of impressions)

  const stages = [
    { label: 'Impressions', value: impressions, color: 'var(--fn-primary)' },
    { label: 'Views', value: views, color: '#8b5cf6' },
    { label: 'Conversions', value: conversions, color: '#10b981' },
  ]

  // Calculate conversion rates
  const impressionToViewRate = impressions > 0 ? ((views / impressions) * 100).toFixed(1) : '0.0'
  const viewToConversionRate = views > 0 ? ((conversions / views) * 100).toFixed(1) : '0.0'

  const hasData = impressions > 0

  if (!hasData) {
    return (
      <Card>
        <CardHeader>
          <h3 className="text-lg font-semibold">Conversion Funnel</h3>
        </CardHeader>
        <CardBody>
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="text-tf-text-muted mb-2">
              <svg
                className="w-16 h-16 mx-auto"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M3 4h18M3 4v11l9 5 9-5V4M3 4l9 5 9-5"
                />
              </svg>
            </div>
            <p className="text-tf-text-muted font-medium">No conversion data yet</p>
            <p className="text-sm text-tf-text-muted mt-1">
              Conversion funnel will appear once funnels receive traffic
            </p>
          </div>
        </CardBody>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <h3 className="text-lg font-semibold">Conversion Funnel</h3>
      </CardHeader>
      <CardBody>
        <div className="space-y-4 py-4">
          {stages.map((stage, index) => {
            const maxValue = stages[0].value || 1
            const widthPercent = (stage.value / maxValue) * 100
            const nextStage = stages[index + 1]
            const dropoffRate =
              nextStage && stage.value > 0
                ? (((stage.value - nextStage.value) / stage.value) * 100).toFixed(1)
                : null

            return (
              <div key={stage.label}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-tf-text-sub">{stage.label}</span>
                  <span className="text-sm font-semibold text-tf-text">
                    {stage.value.toLocaleString()}
                  </span>
                </div>
                <div className="w-full bg-tf-bg-hover rounded-full h-10 relative overflow-hidden">
                  <div
                    className="h-full rounded-full flex items-center justify-center text-white font-medium text-sm transition-all duration-500"
                    style={{
                      width: `${Math.max(widthPercent, 5)}%`,
                      backgroundColor: stage.color,
                    }}
                  >
                    {widthPercent >= 15 && `${widthPercent.toFixed(0)}%`}
                  </div>
                </div>
                {dropoffRate && (
                  <div className="flex items-center justify-between mt-1 text-xs text-tf-text-muted">
                    <span>Drop-off: {dropoffRate}%</span>
                    <span>
                      Conversion:{' '}
                      {index === 0 ? impressionToViewRate : viewToConversionRate}%
                    </span>
                  </div>
                )}
              </div>
            )
          })}
        </div>
        <div className="mt-6 pt-4 border-t border-tf-border">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-tf-text-sub">Overall Conversion Rate</span>
            <span className="text-lg font-bold text-tf-success">
              {metrics.conversionRate.toFixed(2)}%
            </span>
          </div>
        </div>
      </CardBody>
    </Card>
  )
}
